import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  increment,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * ═══════════════════════════════════════════════════════════
 * PROFILS FORUM
 * ═══════════════════════════════════════════════════════════
 */

function getRandomAvatar() {
  const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444'];
  return colors[Math.floor(Math.random() * colors.length)];
}

export async function getOrCreateForumProfile(userId) {
  try {
    const profileRef = doc(db, 'forumProfiles', userId);
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      return { id: profileSnap.id, ...profileSnap.data() };
    }

    const newProfile = {
      userId,
      pseudo: `Utilisateur${Math.floor(Math.random() * 10000)}`,
      avatar: getRandomAvatar(),
      joinedAt: serverTimestamp(),
      threadsCount: 0,
      repliesCount: 0,
      reputation: 0,
    };

    await setDoc(profileRef, newProfile);
    return { id: userId, ...newProfile };
  } catch (error) {
    console.error('Erreur getOrCreateForumProfile:', error);
    throw error;
  }
}

/**
 * Met à jour la réputation d'un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {number} points - Points à ajouter (positif) ou retirer (négatif)
 */
export async function updateReputation(userId, points) {
  try {
    const profileRef = doc(db, 'forumProfiles', userId);
    await updateDoc(profileRef, {
      reputation: increment(points),
    });
  } catch (error) {
    console.error('Erreur updateReputation:', error);
    throw error;
  }
}

export async function updateForumPseudo(userId, newPseudo) {
  try {
    const profileRef = doc(db, 'forumProfiles', userId);
    await updateDoc(profileRef, {
      pseudo: newPseudo,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erreur updateForumPseudo:', error);
    throw error;
  }
}

/**
 * ═══════════════════════════════════════════════════════════
 * THREADS
 * ═══════════════════════════════════════════════════════════
 */

export async function createThread(userId, title, content) {
  try {
    const profile = await getOrCreateForumProfile(userId);

    const threadData = {
      authorId: userId,
      authorPseudo: profile.pseudo,
      authorAvatar: profile.avatar,
      title: title.trim(),
      content: content.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      likes: 0,
      likedBy: [],
      repliesCount: 0,
      lastReplyAt: null,
      isPinned: false,
      isLocked: false,
      isHidden: false,
    };

    const docRef = await addDoc(collection(db, 'threads'), threadData);

    const profileRef = doc(db, 'forumProfiles', userId);
    await updateDoc(profileRef, {
      threadsCount: increment(1),
      reputation: increment(10), // +10 points pour création d'un thread
    });

    return { id: docRef.id, ...threadData };
  } catch (error) {
    console.error('Erreur createThread:', error);
    throw error;
  }
}

export async function getThreads(sortBy = 'recent', limitCount = 50) {
  try {
    let q;

    if (sortBy === 'popular') {
      q = query(
        collection(db, 'threads'),
        where('isHidden', '==', false),
        orderBy('likes', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    } else if (sortBy === 'active') {
      q = query(
        collection(db, 'threads'),
        where('isHidden', '==', false),
        orderBy('lastReplyAt', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    } else {
      q = query(
        collection(db, 'threads'),
        where('isHidden', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Erreur getThreads:', error);
    throw error;
  }
}

export async function getThread(threadId) {
  try {
    const docRef = doc(db, 'threads', threadId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Thread non trouvé');
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Erreur getThread:', error);
    throw error;
  }
}

export async function toggleThreadLike(threadId, userId) {
  try {
    const threadRef = doc(db, 'threads', threadId);
    const threadSnap = await getDoc(threadRef);

    if (!threadSnap.exists()) {
      throw new Error('Thread non trouvé');
    }

    const thread = threadSnap.data();
    const hasLiked = thread.likedBy?.includes(userId);

    if (hasLiked) {
      await updateDoc(threadRef, {
        likes: increment(-1),
        likedBy: arrayRemove(userId),
      });
      // -2 points à l'auteur du thread (perte de like)
      await updateReputation(thread.authorId, -2);
    } else {
      await updateDoc(threadRef, {
        likes: increment(1),
        likedBy: arrayUnion(userId),
      });
      // +2 points à l'auteur du thread (nouveau like)
      await updateReputation(thread.authorId, 2);
    }

    return !hasLiked;
  } catch (error) {
    console.error('Erreur toggleThreadLike:', error);
    throw error;
  }
}

export async function deleteThread(threadId, userId, isAdmin = false) {
  try {
    const threadRef = doc(db, 'threads', threadId);
    const threadSnap = await getDoc(threadRef);

    if (!threadSnap.exists()) {
      throw new Error('Thread non trouvé');
    }

    const thread = threadSnap.data();

    if (thread.authorId !== userId && !isAdmin) {
      throw new Error('Non autorisé');
    }

    const repliesQuery = query(collection(db, 'replies'), where('threadId', '==', threadId));
    const repliesSnapshot = await getDocs(repliesQuery);

    const deletePromises = repliesSnapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    await deleteDoc(threadRef);

    const profileRef = doc(db, 'forumProfiles', thread.authorId);
    await updateDoc(profileRef, {
      threadsCount: increment(-1),
      reputation: increment(-10), // -10 points pour suppression d'un thread
    });
  } catch (error) {
    console.error('Erreur deleteThread:', error);
    throw error;
  }
}

/**
 * ═══════════════════════════════════════════════════════════
 * REPLIES
 * ═══════════════════════════════════════════════════════════
 */

export async function createReply(threadId, userId, content) {
  try {
    const profile = await getOrCreateForumProfile(userId);

    const replyData = {
      threadId,
      authorId: userId,
      authorPseudo: profile.pseudo,
      authorAvatar: profile.avatar,
      content: content.trim(),
      createdAt: serverTimestamp(),
      likes: 0,
      likedBy: [],
      isHidden: false,
    };

    const docRef = await addDoc(collection(db, 'replies'), replyData);

    const threadRef = doc(db, 'threads', threadId);
    await updateDoc(threadRef, {
      repliesCount: increment(1),
      lastReplyAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const profileRef = doc(db, 'forumProfiles', userId);
    await updateDoc(profileRef, {
      repliesCount: increment(1),
      reputation: increment(5), // +5 points pour création d'une réponse
    });

    return { id: docRef.id, ...replyData };
  } catch (error) {
    console.error('Erreur createReply:', error);
    throw error;
  }
}

export async function getReplies(threadId) {
  try {
    const q = query(
      collection(db, 'replies'),
      where('threadId', '==', threadId),
      where('isHidden', '==', false),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Erreur getReplies:', error);
    throw error;
  }
}

export async function toggleReplyLike(replyId, userId) {
  try {
    const replyRef = doc(db, 'replies', replyId);
    const replySnap = await getDoc(replyRef);

    if (!replySnap.exists()) {
      throw new Error('Réponse non trouvée');
    }

    const reply = replySnap.data();
    const hasLiked = reply.likedBy?.includes(userId);

    if (hasLiked) {
      await updateDoc(replyRef, {
        likes: increment(-1),
        likedBy: arrayRemove(userId),
      });
      // -2 points à l'auteur de la réponse (perte de like)
      await updateReputation(reply.authorId, -2);
    } else {
      await updateDoc(replyRef, {
        likes: increment(1),
        likedBy: arrayUnion(userId),
      });
      // +2 points à l'auteur de la réponse (nouveau like)
      await updateReputation(reply.authorId, 2);
    }

    return !hasLiked;
  } catch (error) {
    console.error('Erreur toggleReplyLike:', error);
    throw error;
  }
}

export async function deleteReply(replyId, threadId, userId, isAdmin = false) {
  try {
    const replyRef = doc(db, 'replies', replyId);
    const replySnap = await getDoc(replyRef);

    if (!replySnap.exists()) {
      throw new Error('Réponse non trouvée');
    }

    const reply = replySnap.data();

    if (reply.authorId !== userId && !isAdmin) {
      throw new Error('Non autorisé');
    }

    await deleteDoc(replyRef);

    const threadRef = doc(db, 'threads', threadId);
    await updateDoc(threadRef, {
      repliesCount: increment(-1),
    });

    const profileRef = doc(db, 'forumProfiles', reply.authorId);
    await updateDoc(profileRef, {
      repliesCount: increment(-1),
      reputation: increment(-5), // -5 points pour suppression d'une réponse
    });
  } catch (error) {
    console.error('Erreur deleteReply:', error);
    throw error;
  }
}

/**
 * ═══════════════════════════════════════════════════════════
 * SIGNALEMENTS
 * ═══════════════════════════════════════════════════════════
 */

export async function reportContent(type, itemId, userId, reason, details) {
  try {
    const reportData = {
      type, // 'thread' ou 'reply'
      itemId,
      reportedBy: userId,
      reason,
      details: details?.trim() || '',
      createdAt: serverTimestamp(),
      status: 'pending',
    };

    await addDoc(collection(db, 'reports'), reportData);

    const itemRef = doc(db, type === 'thread' ? 'threads' : 'replies', itemId);
    await updateDoc(itemRef, {
      reportCount: increment(1),
    });
  } catch (error) {
    console.error('Erreur reportContent:', error);
    throw error;
  }
}
